/**
 * Fabrikfunktionen für neu im Client angelegte Entitäten.
 *
 * Die Werte entsprechen den .NET-Standardwerten des jeweiligen Typs:
 * `0` für Zahlen, `false` für `bool`, `null` für Referenz- und Nullable-Typen,
 * der erste Enum-Wert (`0`) für Enums und `DateTime.MinValue` für nicht-nullable Daten.
 */

import {
  ChinaAppSysUserBevorzugteSprache,
  EntityTypes,
  LaufzeitSchritt,
  RechnungArtAufteilung,
  Rechnungsart,
} from '../enums';
import type { BaseEntity } from './base';
import type { Container } from './container';
import type { Document } from './document';
import type { Receipt } from './receipt';
import type { ReceiptAssignment } from './receiptAssignment';
import type {
  ChinaAppSysGroup,
  ChinaAppSysGroupCategory,
  ChinaAppSysGruppenkennzeichen,
  ChinaAppSysLaufzeit,
  ChinaAppSysUser,
  ChinaAppSysView,
} from './system';

/** `default(DateTime)` so, wie System.Text.Json den Wert serialisiert. */
const DEFAULT_DATE_TIME = '0001-01-01T00:00:00';

function createBaseEntity(): BaseEntity {
  return {
    Id: 0,
    Created: DEFAULT_DATE_TIME,
    CreatedByUserId: 0,
    Modified: DEFAULT_DATE_TIME,
    ModifiedByUserId: 0,
    Timestamp: null,
  };
}

export function createContainer(): Container {
  return {
    ...createBaseEntity(),
    AenderungCif: false,
    Anlieferdatum: null,
    AnlieferdatumEmpf: null,
    AnlieferdatumIst: null,
    Anlieferuhrzeit: null,
    Bemerkung: null,
    Bestellpositionen: null,
    Bestellpositionen_Anzahl: null,
    Bestellpositionen_Artikelnummern: null,
    Bestellpositionen_Bezeichnungen1: null,
    Bestellpositionen_CBM: null,
    Bestellpositionen_Mengen: null,
    Bestellpositionen_SageBuchungDatum: null,
    BlNummer: null,
    Containerleerrueckgabe: null,
    ContainerNo: null,
    ContReturnDatum: null,
    ContTyp: null,
    DatumZugestellt: null,
    DepotDatum: null,
    DepotName: null,
    EingangErledigt: false,
    Empfaenger: null,
    Entladedatum: null,
    Eta: null,
    Etd: null,
    FruehestAnlieferdatum: null,
    GateOutDatum: null,
    InWochen: null,
    Kw: null,
    LieferterminContainer: null,
    NaechsteKw: null,
    NlSp: null,
    NlSpPos: null,
    Plombe: null,
    PoD: null,
    Rechnungen: null,
    Reeder: null,
    RueckgabeReferenz: null,
    Schiff: null,
    Spediteur: null,
    TerminBestaetigungCif: null,
  };
}

export function createReceipt(): Receipt {
  return {
    ...createBaseEntity(),
    AnzahlCont: null,
    ArtAufteilung: RechnungArtAufteilung.Anteilig,
    Belegdatum: null,
    Buchdatum: null,
    DL: null,
    GesamtbetragNetto: 0,
    GesamtbetragNettoEuro: null,
    Kurs: null,
    Rechnungsart: Rechnungsart.Seefracht,
    Rechnungsnummer: null,
    Rechnungszuordnung_Container_Bestellposition_BuchungsperiodeWare: null,
    Rechnungszuordnung_Container_Bestellposition_SageBuchungDatum: null,
    Wkz: null,
    Zuordnungen: null,
  };
}

export function createReceiptAssignment(): ReceiptAssignment {
  return {
    ...createBaseEntity(),
    BetragContainerNetto: null,
    ContainerId: 0,
    Container: null,
    Container_AenderungCif: null,
    Container_Anlieferdatum: null,
    Container_AnlieferdatumEmpf: null,
    Container_AnlieferdatumIst: null,
    Container_Anlieferuhrzeit: null,
    Container_Bemerkung: null,
    Container_Bestellpositionen_Anzahl: null,
    Container_Bestellpositionen_Artikelnummern: null,
    Container_Bestellpositionen_Bezeichnungen1: null,
    Container_Bestellpositionen_CBM: null,
    Container_Bestellpositionen_Mengen: null,
    Container_Bestellpositionen_SageBuchungDatum: null,
    Container_BlNummer: null,
    Container_Containerleerrueckgabe: null,
    Container_ContainerNo: null,
    Container_ContReturnDatum: null,
    Container_ContTyp: null,
    Container_DatumZugestellt: null,
    Container_DepotDatum: null,
    Container_DepotName: null,
    Container_EingangErledigt: null,
    Container_Empfaenger: null,
    Container_Entladedatum: null,
    Container_Eta: null,
    Container_Etd: null,
    Container_FruehestAnlieferdatum: null,
    Container_GateOutDatum: null,
    Container_InWochen: null,
    Container_Kw: null,
    Container_NaechsteKw: null,
    Container_NlSp: null,
    Container_NlSpPos: null,
    Container_Plombe: null,
    Container_PoD: null,
    Container_Reeder: null,
    Container_RueckgabeReferenz: null,
    Container_Schiff: null,
    Container_Spediteur: null,
    Container_TerminBestaetigungCif: null,
    Dienstleister: null,
    LieferterminContainer: null,
    Rechnung: null,
    RechnungId: 0,
    Rechnung_ArtAufteilung: null,
    Rechnung_Belegdatum: null,
    Rechnung_Buchdatum: null,
    Rechnung_DL: null,
    Rechnung_GesamtbetragNetto: null,
    Rechnung_GesamtbetragNettoEuro: null,
    Rechnung_Kurs: null,
    Rechnung_Rechnungsart: null,
    Rechnung_Rechnungsnummer: null,
    Rechnung_Wkz: null,
  };
}

export function createDocument(): Document {
  return {
    ...createBaseEntity(),
    Bezeichnung: null,
    Beschreibung: null,
    Binary: null,
    BlobId: null,
    Token: null,
    FileName: null,
    FileExtension: null,
    MimeContentType: null,
    DateiGroesse: 0,
    ContainerId: null,
    Container: null,
    ReceiptId: null,
    Receipt: null,
  };
}

export function createChinaAppSysUser(): ChinaAppSysUser {
  return {
    ...createBaseEntity(),
    Name: null,
    EMail: null,
    Oid: null,
    BevorzugteSprache: ChinaAppSysUserBevorzugteSprache.De,
    HasAnyGroups: false,
    Groups: null,
  };
}

export function createChinaAppSysGroup(): ChinaAppSysGroup {
  return {
    ...createBaseEntity(),
    Bezeichnung: null,
    IstAdministrativ: false,
    Users: null,
    Views: null,
  };
}

export function createChinaAppSysGroupCategory(): ChinaAppSysGroupCategory {
  return {
    ...createBaseEntity(),
    Bezeichnung: null,
    MenuSortierung: 0,
    Views: null,
  };
}

export function createChinaAppSysView(): ChinaAppSysView {
  return {
    ...createBaseEntity(),
    ViewName: null,
    Entity: EntityTypes.ChinaAppSysGroup,
    CategoryId: null,
    Category: null,
    CustomEndpoint: null,
    AdditionalFunctions: null,
    DefaultFilter: null,
    DefaultOrder: null,
    Fields: null,
    Groups: null,
  };
}

export function createChinaAppSysGruppenkennzeichen(): ChinaAppSysGruppenkennzeichen {
  return {
    ...createBaseEntity(),
    Entitiy: EntityTypes.ChinaAppSysGroup,
    Fieldname: null,
    Value: null,
  };
}

export function createChinaAppSysLaufzeit(): ChinaAppSysLaufzeit {
  return {
    ...createBaseEntity(),
    SchrittName: LaufzeitSchritt.Vorlauf,
    DauerTage: 0,
  };
}
